import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ShieldCheck, ShieldAlert, Coins, Clock, Search, Download, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

function Stat({ label, value, tone = "slate", testId, icon }) {
  const bg = { slate: "bg-white", green: "bg-[#F0FDF4]", red: "bg-[#FEF2F2]", indigo: "bg-[#EEF2FF]", amber: "bg-[#FFFBEB]" }[tone];
  return (
    <div className={`card-shell p-5 ${bg}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="data-label">{label}</div>
        {icon}
      </div>
      <div className="mt-2 font-heading font-black text-3xl tracking-tight">{value}</div>
    </div>
  );
}

export default function IncentiveApprovalsPage() {
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState("pending");
  const [matchFilter, setMatchFilter] = useState("__all");
  const [q, setQ] = useState("");
  const [rejectFor, setRejectFor] = useState(null); // record | 'bulk'
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    const [s, r] = await Promise.all([
      api.get("/incentive_ops/summary"),
      api.get("/records", { params: { request_type: "warranty" } }),
    ]);
    setSummary(s.data);
    setRecords(r.data);
    try {
      const u = await api.get("/users");
      const map = {};
      (u.data || []).forEach((x) => (map[x.id] = x));
      setUsers(map);
    } catch { /* sales_incentive role can't list users */ }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (!r.salesperson_user_id) return false;
      if (statusFilter !== "__all" && r.incentive_approval_status !== statusFilter) return false;
      if (matchFilter === "matched" && !r.unique_id_matched) return false;
      if (matchFilter === "unmatched" && r.unique_id_matched !== false) return false;
      if (q) {
        const term = q.toLowerCase();
        const d = r.data || {};
        const blob = [r.ticket_id, d.full_name, d.phone, d.unique_product_id, d.serial_number]
          .filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(term)) return false;
      }
      return true;
    });
  }, [records, statusFilter, matchFilter, q]);

  const toggleRow = (id) => setSelected((prev) => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });
  const toggleAll = () => setSelected((prev) => {
    if (prev.size === filtered.length) return new Set();
    return new Set(filtered.map((r) => r.id));
  });

  const approveOne = async (rec) => {
    if (!rec.unique_id_matched) {
      return toast.error("Cannot approve — Unique ID is unmatched. Ask admin to upload it first.");
    }
    setBusy(true);
    try {
      await api.post(`/records/${rec.id}/approve_incentive`, { approved: true });
      toast.success(`Approved ${rec.ticket_id}`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const rejectSubmit = async () => {
    setBusy(true);
    try {
      if (rejectFor === "bulk") {
        const ids = Array.from(selected);
        await api.post("/records/bulk_approve_incentive", { record_ids: ids, approved: false, reason: rejectReason });
        toast.success(`Rejected ${ids.length} record${ids.length === 1 ? "" : "s"}`);
        setSelected(new Set());
      } else if (rejectFor?.id) {
        await api.post(`/records/${rejectFor.id}/approve_incentive`, { approved: false, reason: rejectReason });
        toast.success(`Rejected ${rejectFor.ticket_id}`);
      }
      setRejectFor(null);
      setRejectReason("");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  const bulkApprove = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBusy(true);
    try {
      const r = await api.post("/records/bulk_approve_incentive", { record_ids: ids, approved: true });
      let msg = `Approved ${r.data.updated}`;
      if (r.data.skipped_unmatched) msg += ` · Skipped ${r.data.skipped_unmatched} unmatched`;
      toast.success(msg);
      setSelected(new Set());
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk approve failed");
    } finally {
      setBusy(false);
    }
  };

  const agentName = (id) => users[id]?.name || id?.slice(0, 8) + "…" || "—";

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="data-label mb-1">Incentive approvals</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">Sales Incentives</h1>
          <p className="text-sm text-[#71717A] mt-1">
            Review each product registration submitted by a Sales Agent. Approve to release the incentive; reject with a reason if the sale is invalid.
          </p>
        </div>
        <Link to="/unique-ids" className="text-sm text-[#4F46E5] hover:underline flex items-center gap-1" data-testid="link-unique-ids">
          <Download className="w-4 h-4" /> View Unique ID master list
        </Link>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Pending approval" value={summary.counts.pending} tone="amber" testId="stat-pending" icon={<Clock className="w-4 h-4 text-[#D97706]" />} />
          <Stat label="Approved" value={summary.counts.approved} tone="green" testId="stat-approved" icon={<CheckCircle2 className="w-4 h-4 text-[#059669]" />} />
          <Stat label="Unmatched IDs" value={summary.counts.unmatched} tone="red" testId="stat-unmatched" icon={<ShieldAlert className="w-4 h-4 text-[#DC2626]" />} />
          <Stat
            label="Approved incentive paid"
            value={`₹${Number(summary.totals.approved_incentive || 0).toLocaleString("en-IN")}`}
            tone="indigo"
            testId="stat-approved-amount"
            icon={<Coins className="w-4 h-4 text-[#4F46E5]" />}
          />
        </div>
      )}

      <div className="card-shell p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <Input
            className="pl-9"
            placeholder="Search by ticket, customer, serial, unique ID…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="approvals-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="approvals-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="__all">All</SelectItem>
          </SelectContent>
        </Select>
        <Select value={matchFilter} onValueChange={setMatchFilter}>
          <SelectTrigger className="w-[180px]" data-testid="approvals-match-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All Unique IDs</SelectItem>
            <SelectItem value="matched">Matched only</SelectItem>
            <SelectItem value="unmatched">Unmatched only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selected.size > 0 && (
        <div className="card-shell p-3 flex items-center justify-between bg-[#F0FDF4] border-[#86EFAC]" data-testid="bulk-approve-bar">
          <div className="text-sm">
            <span className="font-medium">{selected.size}</span> record{selected.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} data-testid="clear-selection">Clear</Button>
            <Button size="sm" variant="outline" onClick={() => { setRejectFor("bulk"); setRejectReason(""); }} data-testid="bulk-reject-btn">
              <XCircle className="w-4 h-4 mr-1" /> Reject selected
            </Button>
            <Button size="sm" className="bg-[#059669] hover:bg-[#047857] text-white" onClick={bulkApprove} disabled={busy} data-testid="bulk-approve-btn">
              <ShieldCheck className="w-4 h-4 mr-1" /> Approve selected
            </Button>
          </div>
        </div>
      )}

      <div className="card-shell overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <Checkbox
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onCheckedChange={toggleAll}
                    data-testid="approvals-select-all"
                  />
                </th>
                <th>Ticket</th>
                <th>Customer</th>
                <th>Unique ID</th>
                <th>Match</th>
                <th>Agent</th>
                <th className="text-right">Incentive</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody data-testid="approvals-tbody">
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="p-10 text-center text-[#71717A] text-sm">
                  Nothing to review right now.
                </td></tr>
              ) : filtered.map((r) => {
                const d = r.data || {};
                const status = r.incentive_approval_status || "pending";
                return (
                  <tr key={r.id} data-testid={`approval-row-${r.ticket_id}`}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleRow(r.id)} data-testid={`approval-check-${r.ticket_id}`} />
                    </td>
                    <td className="font-mono text-xs font-medium">
                      <Link to={`/records/${r.id}`} className="text-[#4F46E5] hover:underline">{r.ticket_id}</Link>
                    </td>
                    <td>
                      <div className="font-medium">{d.full_name || "—"}</div>
                      <div className="text-xs text-[#71717A]">{d.phone || d.email || ""}</div>
                    </td>
                    <td className="font-mono text-xs">{d.unique_product_id || "—"}</td>
                    <td>
                      {r.unique_id_matched === true && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-mono bg-[#DCFCE7] text-[#166534]">matched</span>
                      )}
                      {r.unique_id_matched === false && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-mono bg-[#FEE2E2] text-[#991B1B]">unmatched</span>
                      )}
                      {r.possible_duplicate && (
                        <span
                          className="ml-1 inline-flex items-center gap-0.5 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-mono bg-[#FEF3C7] text-[#92400E]"
                          title={`Same phone appeared on ${r.duplicate_of?.ticket_id || "another ticket"} within the last 30 days.`}
                          data-testid={`duplicate-badge-${r.ticket_id}`}
                        >
                          <AlertTriangle className="w-3 h-3" /> dup
                        </span>
                      )}
                    </td>
                    <td className="text-sm">{agentName(r.salesperson_user_id)}</td>
                    <td className="text-right font-mono">₹{Number(r.incentive_amount || 0).toLocaleString("en-IN")}</td>
                    <td>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-mono ${
                        status === "approved" ? "bg-[#DCFCE7] text-[#166534]" :
                        status === "rejected" ? "bg-[#FEE2E2] text-[#991B1B]" :
                        "bg-[#FEF3C7] text-[#92400E]"
                      }`}>{status}</span>
                    </td>
                    <td className="whitespace-nowrap">
                      {status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            className="bg-[#059669] hover:bg-[#047857] text-white mr-1"
                            onClick={() => approveOne(r)}
                            disabled={busy || !r.unique_id_matched}
                            title={!r.unique_id_matched ? "Unique ID is unmatched — cannot approve" : ""}
                            data-testid={`approve-${r.ticket_id}`}
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setRejectFor(r); setRejectReason(""); }}
                            data-testid={`reject-${r.ticket_id}`}
                          >
                            <XCircle className="w-3 h-3 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      {status === "rejected" && r.incentive_rejection_reason && (
                        <div className="text-xs text-[#DC2626]" title={r.incentive_rejection_reason}>
                          {r.incentive_rejection_reason.slice(0, 30)}{r.incentive_rejection_reason.length > 30 ? "…" : ""}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {rejectFor === "bulk" ? `Reject ${selected.size} record${selected.size === 1 ? "" : "s"}` : `Reject ${rejectFor?.ticket_id}`}
            </DialogTitle>
            <DialogDescription>
              The agent will see this reason on their earnings page.
            </DialogDescription>
          </DialogHeader>
          <Label className="data-label mb-1.5 block">Reason</Label>
          <Textarea
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. duplicate registration / product returned / SOP mismatch"
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)} data-testid="reject-cancel">Cancel</Button>
            <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" onClick={rejectSubmit} disabled={busy} data-testid="reject-confirm">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
